import { useEffect } from "react";
import { SITE_ORIGIN, absoluteUrl } from "@/lib/site";
const DEFAULT_OG_IMAGE =
  "https://cdn.prod.website-files.com/686229256a807206c1874545/68bbbb5275415f71d5848a00_Updated%20OG%20Image.png";

interface SEOConfig {
  title: string;
  description?: string;
  canonical?: string;
  noindex?: boolean;
  ogTitle?: string;
  ogDescription?: string;
  ogImage?: string;
  ogType?: string;
  twitterCard?: "summary" | "summary_large_image";
  jsonLd?: Record<string, unknown>;
}

function setMetaTag(attribute: string, key: string, content: string) {
  let el = document.querySelector(`meta[${attribute}="${key}"]`);
  if (!el) {
    el = document.createElement("meta");
    el.setAttribute(attribute, key);
    document.head.appendChild(el);
  }
  el.setAttribute("content", content);
}

function setCanonical(href: string) {
  let el = document.querySelector('link[rel="canonical"]') as HTMLLinkElement | null;
  if (!el) {
    el = document.createElement("link");
    el.setAttribute("rel", "canonical");
    document.head.appendChild(el);
  }
  el.href = href;
}

const JSON_LD_ID = "seo-json-ld";

function setJsonLd(data: Record<string, unknown> | undefined) {
  let el = document.getElementById(JSON_LD_ID) as HTMLScriptElement | null;
  if (!data) {
    el?.remove();
    return;
  }
  if (!el) {
    el = document.createElement("script");
    el.id = JSON_LD_ID;
    el.type = "application/ld+json";
    document.head.appendChild(el);
  }
  el.textContent = JSON.stringify(data);
}

/**
 * Lightweight per-page SEO hook. Updates document title, meta tags,
 * OG/Twitter cards, canonical URL, and optional JSON-LD.
 *
 * On unmount, restores the defaults from index.html.
 */
export function useSEO(config: SEOConfig) {
  useEffect(() => {
    const {
      title,
      description,
      canonical,
      ogTitle,
      ogDescription,
      ogImage,
      ogType = "website",
      twitterCard = "summary_large_image",
      jsonLd,
      noindex = false,
    } = config;

    const fullTitle = title.includes("BollyRhythm")
      ? title
      : `${title} | BollyRhythm`;

    // Title
    document.title = fullTitle;

    // Meta description
    if (description) {
      setMetaTag("name", "description", description);
    }

    // Canonical
    const canonicalUrl = canonical
      ? absoluteUrl(canonical)
      : absoluteUrl(window.location.pathname);
    setCanonical(canonicalUrl);

    // Open Graph
    setMetaTag("property", "og:title", ogTitle ?? fullTitle);
    setMetaTag("property", "og:description", ogDescription ?? description ?? "");
    setMetaTag("property", "og:image", ogImage ?? DEFAULT_OG_IMAGE);
    setMetaTag("property", "og:url", canonicalUrl);
    setMetaTag("property", "og:type", ogType);

    // Twitter
    setMetaTag("name", "twitter:card", twitterCard);
    setMetaTag("name", "twitter:title", ogTitle ?? fullTitle);
    setMetaTag("name", "twitter:description", ogDescription ?? description ?? "");
    setMetaTag("name", "twitter:image", ogImage ?? DEFAULT_OG_IMAGE);

    // JSON-LD
    setJsonLd(jsonLd);

    if (noindex) {
      setMetaTag("name", "robots", "noindex, nofollow");
    }

    // Cleanup: restore defaults on unmount
    return () => {
      document.title = "BollyRhythm - Our Moves. Your Dance.";
      const defaultDesc =
        "Choreographed Bollywood tutorials for all skill levels--perfect for South Asian events. Learn and create performances anytime, anywhere, and arrive confident.";
      const defaultOgDesc = "Create unforgettable performances without the stress.";
      setMetaTag("name", "description", defaultDesc);
      setCanonical(SITE_ORIGIN);
      setMetaTag("property", "og:title", "BollyRhythm - Our Moves. Your Dance.");
      setMetaTag("property", "og:description", defaultOgDesc);
      setMetaTag("property", "og:image", DEFAULT_OG_IMAGE);
      setMetaTag("property", "og:url", SITE_ORIGIN);
      setMetaTag("property", "og:type", "website");
      setMetaTag("name", "twitter:card", "summary_large_image");
      setMetaTag("name", "twitter:title", "BollyRhythm - Our Moves. Your Dance.");
      setMetaTag("name", "twitter:description", defaultOgDesc);
      setMetaTag("name", "twitter:image", DEFAULT_OG_IMAGE);
      setJsonLd(undefined);
      if (noindex) {
        document.querySelector('meta[name="robots"]')?.remove();
      }
    };
  }, [
    config.title,
    config.description,
    config.canonical,
    config.noindex,
    config.ogTitle,
    config.ogDescription,
    config.ogImage,
    config.ogType,
    config.twitterCard,
    config.jsonLd,
  ]);
}
